/**
 * The communication history — the SHAPE of the conversation, not its content.
 *
 * `sellers.ts` deliberately leaves note bodies out of this payload: §14.2
 * keeps the text of a negotiation note off any surface that does not need it,
 * and what this panel needs is the rhythm — who reached out, on which channel,
 * on which day, and what came of it. There is no note text to render here and
 * that is on purpose, not an omission.
 *
 * `happened_at` is a `YYYY-MM-DD` business date with no time component, and
 * 0055 explains why: `created_at` is when the row was TYPED, and somebody logs
 * Tuesday's call on Thursday. So the timeline is grouped and labelled by day,
 * never by hour — the same reason `seller_responsiveness` is measured in whole
 * days rather than pretending to an hour's resolution it cannot derive.
 */

import { useCallback, useState } from 'react'
import Icon from '../Icon'
import { useLocale, type Locale } from '../../lib/i18n'
import { dateText, TONES } from '../../lib/format'
import { channelLabel, directionLabel, outcomeLabel } from '../../lib/domain'
import { Chip, Empty, S } from './ui'
import type { SellerTimelineRow } from './types'

const EN = {
  heading: 'Conversation',
  none: 'Nothing has been logged yet.',
  offer: 'Offer',
  more: 'Show all',
  less: 'Show fewer',
  noText: 'Note text is kept off this screen on purpose.',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  heading: 'Conversación',
  none: 'Todavía no se ha anotado nada.',
  offer: 'Oferta',
  more: 'Ver todo',
  less: 'Ver menos',
  noText: 'El texto de las notas se queda fuera de esta pantalla a propósito.',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

const FIRST_PAGE = 8

export default function SellerTimeline({ rows }: { rows: SellerTimelineRow[] }) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])
  const [all, setAll] = useState(false)

  const shown = all ? rows : rows.slice(0, FIRST_PAGE)

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>{t('heading')}</h3>

      {rows.length === 0 ? (
        <Empty>{t('none')}</Empty>
      ) : (
        <>
          {shown.map((row) => (
            <div key={row.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid var(--line)' }}>
              <span style={{ flex: '0 0 auto', color: row.direction === 'inbound' ? 'var(--score-strong)' : 'var(--muted)' }}>
                <Icon name={row.direction === 'inbound' ? 'back' : 'chevron'} size={16} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>
                  {directionLabel(row.direction, locale)} · {channelLabel(row.channel, locale)}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>
                  {dateText(row.happened_at, locale)}
                </span>
              </span>
              <span style={{ ...S.chipRow, flex: '0 0 auto', justifyContent: 'flex-end' }}>
                {row.outcome && <Chip tone={TONES.quiet} outlined>{outcomeLabel(row.outcome, locale)}</Chip>}
                {row.is_offer === 1 && <Chip tone={TONES.money}>{t('offer')}</Chip>}
              </span>
            </div>
          ))}

          {rows.length > FIRST_PAGE && (
            <button
              type="button"
              onClick={() => setAll(!all)}
              style={{ ...S.button, width: '100%', marginTop: 8 }}
            >
              {all ? t('less') : `${t('more')} (${rows.length})`}
            </button>
          )}

          <p style={S.note}>{t('noText')}</p>
        </>
      )}
    </div>
  )
}
